import { computeIfAbsent } from './../../common/utils/map-utils';
import { Optional } from './../../common/utils/optional';
import { GenericSearchDataset, GenericSearchMetaData } from './../../common/genericSearch/model/genericsearchdata';
import {
    Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter
} from '@angular/core';
import { HisSearchResultComponent, HisUiService, HisBaseGlobalsService, DdEntryMerged } from 'hisng';

@Component( {
    selector: 'erp-jobadvertisement-search-result-list',
    templateUrl: './jobadvertisement-search-result-list.component.html'
} )
export class JobAdvertisementSearchResultListComponent implements OnInit, OnChanges {

    @ViewChild( HisSearchResultComponent, { static: true } ) searchResultListComponent!: HisSearchResultComponent;
    @Input() data: GenericSearchDataset[] = [];
    @Output() readonly page = new EventEmitter<{ first: number; rows: number }>();

    categories: Category[] = [];
    isRefNumberVisible = true;
    isValidToVisible = true;

    constructor(
        readonly hisSearchResultComponent: HisSearchResultComponent,
        private readonly hisUiService: HisUiService,
        private readonly hisBaseGlobalsService: HisBaseGlobalsService
    ) { }

    ngOnInit() {
        this.page.emit( { first: 0, rows: this.data.length } );

        let ddRefNumber: DdEntryMerged = this.hisBaseGlobalsService.getDd('rm.erp.jobadvertisement.searchresultlist.refNumber');
        this.isRefNumberVisible = this.isVisible(ddRefNumber);

        let ddValidTo: DdEntryMerged = this.hisBaseGlobalsService.getDd('rm.erp.jobadvertisement.searchresultlist.validTo');
        this.isValidToVisible = this.isVisible(ddValidTo);
    }

    ngOnChanges( changes: SimpleChanges ) {
        // eslint-disable-next-line @typescript-eslint/dot-notation,dot-notation
        if ( changes['data'] ) {
            if ( this.data ) {
                this.categories = this.parse( this.data );
            }
        }
    }

    private isVisible(ddEntry: DdEntryMerged): boolean {
        if ( ddEntry && ddEntry.hidden ) {
            return false;
        }
        return true;
    }

    private parse( jobadvertisementDatasets: GenericSearchDataset[] ): Category[] {
        const categories = new Map<string, Category>();

        jobadvertisementDatasets.forEach( ( jobadvertisementDataset ) => {
            const joinedDataSet = new Map<string, string>();

            jobadvertisementDataset.metadata.forEach( ( metadata: GenericSearchMetaData, index: number ) => {
                const value = jobadvertisementDataset.values[index];
                if ( value ) {
                    joinedDataSet.set( metadata.id, value );
                }
            } );

            const catKey = joinedDataSet.get( 'JobAdvertisement.jobAdvertisementCategory.uniquename' ) ?? 'XXX';
            const sortOrder = Optional.ofNullable( joinedDataSet.get( 'JobAdvertisement.jobAdvertisementCategory.sortorder' ) )
                .map( ( s ) => parseInt( s, 10 ) )
                .orElse( Number.MAX_SAFE_INTEGER );
            const longText = joinedDataSet.get( 'JobAdvertisement.jobAdvertisementCategory.longtext' )
                ?? this.hisUiService.translate( 'rm.erp.jobadvertisement.category.defaultcategory' );

            const category = computeIfAbsent( categories, catKey, ( _ ) => new Category( sortOrder, catKey, longText ) );

            const jobAdvertisement = JobAdvertisement.parse( joinedDataSet );
            if ( jobAdvertisement ) {
                category.jobAdvertisements.push( jobAdvertisement );
            }
        } );
        const result = [...categories.values()];
        result.sort( ( a, b ) => a.sortOrder < b.sortOrder ? -1 : a.sortOrder === b.sortOrder ? 0 : 1 );
        return result;
    }
}

class Category {
    readonly jobAdvertisements: JobAdvertisement[] = [];

    constructor(
        readonly sortOrder: number,
        readonly uniquename: string,
        readonly longText: string,
    ) { }
}

class JobAdvertisement {

    private constructor(
        readonly id: number,
        readonly shortDescription: string | null,
        readonly refNumber: string | null,
        readonly validTo: Date | null,
    ) { }

    static parse( joinedDataset: Map<string, string> ): JobAdvertisement | null {
        const idString = joinedDataset.get( 'JobAdvertisement.id' );
        const id: number | null = idString != null ? parseInt( idString, 10 ) : null;

        const shortDescription: string | null = joinedDataset.get( 'JobAdvertisement.shortDescription' ) ?? null;
        const refNumber: string | null = joinedDataset.get( 'JobAdvertisement.refNumber' ) ?? null;

        const validToString = joinedDataset.get( 'JobAdvertisement.validTo' );
        const validTo: Date | null = validToString != null ? new Date( validToString ) : null;

        if ( id != null ) {
            return new JobAdvertisement( id, shortDescription, refNumber, validTo );
        } else {
            return null;
        }
    }
}
